import { API } from "aws-amplify";

const API_NAME = "IntegrationsAPI"; // Add this API to your Amplify config (aws-exports)
const BASE_ZOHO_PATH = "/integrations/zoho";

export default class IntegrationsService {
    // ============================================
    // Zoho Books Methods (Legacy - Maintained for backward compatibility)
    // ============================================

    async zohoConnect(redirectUri) {
        // Amplify will attach the configured auth (Cognito token / IAM) for this API if configured
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/connect`, { queryStringParameters: { redirectUri } });
    }

    async zohoGetOrganizations() {
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/organizations`);
    }

    async zohoGetProfitAndLossReport() {
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/reports/profitandloss`, {
            queryStringParameters: {},
        });
    }

    // ============================================
    // Zoho Projects Methods
    // ============================================

    /**
     * Get Zoho Projects OAuth connection URL
     * @param {string} redirectUri - Redirect URI for OAuth callback
     * @returns {Promise<{authorizationUrl: string}>}
     */
    async zohoProjectsConnect(redirectUri) {
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/projects/connect`, { 
            queryStringParameters: { redirectUri } 
        });
    }

    /**
     * Get list of projects for connected Zoho Projects integration
     * @returns {Promise<{projects: Array, portal: Object}>}
     */
    async zohoProjectsList() {
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/projects/list`);
    }

    /**
     * Get all tasks across all Zoho Projects for the current tenant
     * @returns {Promise<{tasks: Array, projects_count: number}>}
     */
    async zohoProjectsTasks() {
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/projects/tasks`);
    }

    /**
     * Get all portal-level users for the connected Zoho Projects integration
     * @param {string} [type] - portal_user | client_user | client_contact
     * @param {string} [viewType] - active | inactive | ...
     * @returns {Promise<{users: Array, count: number}>}
     */
    async zohoProjectsUsers({ type, viewType } = {}) {
        const qs = {};
        if (type) qs.type = type;
        if (viewType) qs.view_type = viewType;
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/projects/users`, { queryStringParameters: qs });
    }

    /**
     * Get users per project across all projects
     * @returns {Promise<{project_users: Array, projects_count: number, total_count: number}>}
     */
    async zohoProjectsProjectUsers() {
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/projects/project-users`);
    }

    /**
     * Get time logs per project across all projects
     * @param {string} [viewType] - day | week | month | customdate | projectspan
     * @param {string} [startDate] - YYYY-MM-DD
     * @param {string} [endDate] - YYYY-MM-DD
     * @returns {Promise<{time_logs: Array, log_hours: Object, projects_count: number}>}
     */
    async zohoProjectsTimeLogs({ viewType, startDate, endDate } = {}) {
        const qs = {};
        if (viewType) qs.view_type = viewType;
        if (startDate) qs.start_date = startDate;
        if (endDate) qs.end_date = endDate;
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/projects/timelogs`, { queryStringParameters: qs });
    }

    /**
     * Send a chat message about Zoho Projects data to the AI assistant
     * @param {string} message - User's question
     * @returns {Promise<{message: string}>}
     */
    async zohoProjectsChat(message) {
        return API.post(API_NAME, `${BASE_ZOHO_PATH}/projects/chat`, { body: { message } });
    }

    /**
     * Get AI-generated operational insights from Zoho Projects data
     * @returns {Promise<{insights: Array<{level: string, title: string, description: string}>}>}
     */
    async zohoProjectsInsights() {
        return API.get(API_NAME, `${BASE_ZOHO_PATH}/projects/insights`, {});
    }

    // ============================================
    // Generic Integration Methods
    // ============================================

    /**
     * Get status of all integrations for current user
     * @returns {Promise<Array>}
     */
    async getIntegrationStatus() {
        return API.get(API_NAME, `/integrations/status`);
    }

    /**
     * Disconnect a specific integration
     * @param {string} provider - Provider name (e.g., "ZOHO")
     * @param {string} [product] - Product name (e.g., "BOOKS", "PROJECTS")
     * @returns {Promise<Object>}
     */
    async disconnectIntegration(provider, product) {
        const productPath = product ? `/${product.toLowerCase()}` : "";
        return API.del(API_NAME, `/integrations/${provider.toLowerCase()}${productPath}`);
    }
}
